// Shared building blocks for the client analytics dashboard.
// Ports the Claude Design passes into real components on the Core design tokens.
// No hardcoded hex — named theme tokens only.

import { ReactNode } from "react";
import { Area, AreaChart, ResponsiveContainer } from "recharts";
import { TrendChart } from "@/components/dashboard/TrendChart";
import type { RankBandPoint, TrendPoint } from "@/types/clientAnalytics";

export function formatNum(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  return n.toLocaleString();
}

// SINGLE SOURCE OF TRUTH for money (Carl's ruling, 2026-08-10): four money
// formatters were live across this directory and two rendered on the same
// page for the same client — DealPipelinePanel's local `formatK` ("$6837.1k",
// no thousands separator, read like a typo), DealTable's local `formatAmount`
// (always 2 decimals, so a whole-dollar deal printed "$0.00"), and
// EcommerceRevenuePanel's local `formatMoney` (its own "$Xk" compaction).
// Target shape, confirmed against a real KPI tile (Tyler McBroom's CRM
// "Amount"): "$6,837,093" — full comma-grouped, never abbreviated, no
// trailing ".00". Every money value in this directory now routes through
// this one function; the other three are deleted, not just unused.
//
// `fractionDigits` defaults to 0 (existing behavior, e.g. "$12,450" ad-spend
// hero tiles) -- pass 2 for sub-dollar unit values like CPC/CPM ("$0.97"),
// where rounding to whole dollars silently collapses to "$1" and destroys
// the number, or for an individual-record amount that actually carries
// cents (a caller-computed "does this value have cents?" check, never a
// blanket always-2-decimals rule — see DealTable's formatDealAmount for the
// pattern: whole dollars by default, cents only when non-zero, never ".00").
// Optional + defaulted so every existing call site is unchanged.
//
// `currencyDisplay` folds in EcommerceRevenuePanel's old CURRENCY_SYMBOLS
// map (USD/CAD/AUD/NZD -> "$", GBP -> "£", EUR -> "€") as a real Intl
// option instead of a second hand-rolled lookup table: Intl's default
// "symbol" display renders CAD as "CA$"/AUD as "A$"/NZD as "NZ$" in the
// en-US locale (technically correct, but every one of AA's own dollar-
// denominated reference reports shows a plain "$" regardless of which
// dollar), while "narrowSymbol" gives the plain "$" that map wanted for
// every dollar currency and still renders GBP/EUR correctly (£/€ are
// already narrow). Defaulted to "narrowSymbol" rather than "symbol" since
// every live call site today is USD, where the two render identically
// ("$") -- so this is a no-op for every currently-live tenant and only
// changes behavior the day a non-USD dollar currency actually shows up.
export function formatMoney(
  n: number | null | undefined,
  currency = "USD",
  fractionDigits = 0,
  currencyDisplay: "symbol" | "narrowSymbol" | "code" = "narrowSymbol",
): string {
  if (n === null || n === undefined) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    currencyDisplay,
    maximumFractionDigits: fractionDigits,
    minimumFractionDigits: fractionDigits,
  }).format(n);
}

// `decimals` defaults to 0 (existing behavior -- trend deltas like "8%"
// don't need fractional precision). Pass 2 for small-magnitude rate values
// like CTR ("0.42%"), where rounding to a whole percent collapses any CTR
// under 0.5% to "0%". Optional + defaulted so every existing call site is
// unchanged.
export function formatPct(n: number | null | undefined, decimals = 0): string {
  if (n === null || n === undefined) return "—";
  return `${(n * 100).toFixed(decimals)}%`;
}

// Compact large counts for KPI tiles that would otherwise overflow at
// half-viewport width (AA reference: Impressions "838.5 K", not "838,500")
// -- reserved for tiles where AA's own reference report abbreviates; most
// tiles (Clicks, etc.) keep full formatNum() precision, matching the
// reference exactly rather than compacting everything uniformly. Intl's
// compact notation already drops a trailing ".0" (800000 -> "800K") so no
// extra rounding logic is needed here, just the space AA's report renders
// before the unit letter that Intl doesn't include on its own.
export function formatCompactNum(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  try {
    return new Intl.NumberFormat("en-US", {
      notation: "compact",
      maximumFractionDigits: 1,
    })
      .format(n)
      .replace(/([KMBT])$/, " $1");
  } catch {
    return n.toLocaleString();
  }
}

// GA4 "Average Session Duration" KPI tile format -- AA renders this as
// hh:mm:ss ("00:06:52"), not a raw seconds count. Backend sends plain
// seconds (client_analytics.py docstring: "format to hh:mm:ss here in the
// frontend") per this repo's backend-returns-plain-data / frontend-formats
// split. null/undefined/non-finite -> "—", same convention as formatNum.
export function formatDurationHMS(totalSeconds: number | null | undefined): string {
  if (totalSeconds === null || totalSeconds === undefined || !Number.isFinite(totalSeconds)) {
    return "—";
  }
  const s = Math.max(0, Math.round(totalSeconds));
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
}

// GA4 landing-pages table "User Engagement" column format -- AA renders the
// per-page TOTAL accumulated engagement duration (not hh:mm:ss like the
// account-level KPI tile above) as "4d 5h 14m 17s", dropping any leading
// zero units rather than always showing all four. Same plain-seconds-in
// convention as formatDurationHMS.
export function formatDurationLong(totalSeconds: number | null | undefined): string {
  if (totalSeconds === null || totalSeconds === undefined || !Number.isFinite(totalSeconds)) {
    return "—";
  }
  const s = Math.max(0, Math.round(totalSeconds));
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (days > 0 || hours > 0) parts.push(`${hours}h`);
  if (days > 0 || hours > 0 || minutes > 0) parts.push(`${minutes}m`);
  parts.push(`${seconds}s`);
  return parts.join(" ");
}

// === THE shared date parser. Use this; never `new Date(someString)`. ===
//
// `new Date("YYYY-MM-DD")` parses as UTC MIDNIGHT per the ES spec (a
// date-ONLY ISO string is UTC; a full datetime string with no offset is
// local). Formatting that with a local-timezone accessor therefore renders
// the PREVIOUS day everywhere west of UTC -- "2026-05-12" showed as "May 11"
// in Chicago. That defect shipped in five separate call sites, each of which
// had hand-rolled its own parse, so this is the single source of truth.
//
// Date-only strings are constructed at LOCAL midnight from the parts. Full
// timestamps are passed through unchanged -- they already carry an offset (or
// are explicitly UTC) and must not be re-anchored. Anything unparseable
// returns null so the caller decides its own fallback, rather than leaking a
// literal "Invalid Date" into the UI.
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

export function parseLocalDate(raw?: string | null): Date | null {
  if (!raw) return null;
  const s = String(raw).trim();
  if (!s) return null;
  let d: Date;
  if (DATE_ONLY.test(s)) {
    const [y, m, day] = s.split("-").map(Number);
    d = new Date(y, m - 1, day);
    // Reject impossible calendar dates ("2026-02-31" would roll to Mar 3).
    if (d.getFullYear() !== y || d.getMonth() !== m - 1 || d.getDate() !== day) return null;
  } else {
    d = new Date(s);
  }
  return Number.isNaN(d.getTime()) ? null : d;
}

// Formats one date-only-or-timestamp value. Returns null when unparseable —
// never the string "Invalid Date".
export function formatLocalDate(
  raw?: string | null,
  opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric", year: "numeric" },
): string | null {
  const d = parseLocalDate(raw);
  return d ? d.toLocaleDateString("en-US", opts) : null;
}

// Formats an inclusive range. Returns null if either bound is missing or
// unparseable, so callers never render half a range or a fabricated one.
export function formatLocalDateRange(
  start?: string | null,
  end?: string | null,
  opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric", year: "numeric" },
): string | null {
  const s = formatLocalDate(start, opts);
  const e = formatLocalDate(end, opts);
  return s && e ? `${s} – ${e}` : null;
}

// "2026-08-06" -> "6 Aug" -- compact x-axis tick label for a REAL daily
// TrendChart (not a blanked TrendSparkline), shared so any panel plotting a
// visible daily axis (Meta Ads Clicks today, a future Google Ads equivalent)
// gets the same tick format.
export function formatDayTick(dateStr: string): string {
  const d = parseLocalDate(dateStr);
  if (!d) return dateStr;
  return d.toLocaleDateString("en-US", { day: "numeric", month: "short" });
}

// `over_time` buckets are monthly ("YYYY-MM") for some sections and daily
// (full ISO date) for others -- see TrendPoint.date in clientAnalytics.ts.
// A trailing MONTHLY bucket is "partial" whenever the dashboard's
// period_end falls before that month's last calendar day: a period ending
// 2026-08-05 only covers 5 of August's 31 days. Diffing that partial bucket
// against a full prior month is not a real trend, it's a calendar artifact
// -- the confirmed real-world case is ALIBI Music traffic reading as a
// fabricated "-84%" collapse from 5 days of August vs. a full July. Daily
// buckets never have this problem: a daily point IS the day it labels,
// complete by construction, even when it's the series' last day.
//
// Chosen rule (documented per the ticket): EXCLUDE the partial trailing
// bucket and compare the last two COMPLETE buckets, rather than trying to
// scale it against a same-elapsed-fraction estimate of the prior bucket --
// the series only carries one total per month, so any "Aug 1-5 vs Jul 1-5"
// estimate would have to assume a uniform distribution across the month,
// which is itself a fabrication risk. Excluding is the honest option.
function isMonthlyBucketLabel(date: string): boolean {
  return /^\d{4}-\d{2}$/.test(date);
}

function trailingBucketIsPartial(lastDate: string, periodEnd?: string | null): boolean {
  if (!periodEnd || !isMonthlyBucketLabel(lastDate)) return false;
  const endMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(periodEnd);
  if (!endMatch) return false;
  const [, endYearStr, endMonthStr, endDayStr] = endMatch;
  const endYear = Number(endYearStr);
  const endMonth = Number(endMonthStr);
  const endDay = Number(endDayStr);
  const [labelYear, labelMonth] = lastDate.split("-").map(Number);
  if (labelYear !== endYear || labelMonth !== endMonth) return false;
  const daysInMonth = new Date(endYear, endMonth, 0).getDate();
  return endDay < daysInMonth;
}

// Drops a still-in-progress trailing bucket (see trailingBucketIsPartial
// above) so callers diff/plot only complete, comparable buckets. Shared by
// computeTrendDeltaPct and TrendSparkline so the badge and the chart next to
// it never disagree about whether the last point is real.
function usableTrendPoints(
  points: TrendPoint[],
  periodEnd?: string | null,
): TrendPoint[] {
  const last = points[points.length - 1];
  return trailingBucketIsPartial(last.date, periodEnd) ? points.slice(0, -1) : points;
}

// AA per-cell trend badge (P3-3): a small coloured up/down % badge next to a
// metric, computed period-over-period from that metric's own `over_time`
// series. Never fabricates -- returns null (no badge) whenever the series
// can't support a real delta (fewer than 2 comparable buckets after
// dropping a partial trailing one), matching the "$44,372.85 read as $0"
// and "rank 0 reads as better than #1" lesson from earlier today: a wrong
// number that LOOKS real is worse than a missing one.
export function computeTrendDeltaPct(
  points: TrendPoint[] | null | undefined,
  periodEnd?: string | null,
): number | null {
  if (!points || points.length < 2) return null;
  const usable = usableTrendPoints(points, periodEnd);
  if (usable.length < 2) return null;
  const prev = usable[usable.length - 2].value;
  const curr = usable[usable.length - 1].value;
  if (!Number.isFinite(prev) || !Number.isFinite(curr) || prev === 0) return null;
  return ((curr - prev) / Math.abs(prev)) * 100;
}

// What computeTrendDeltaPct actually just diffed -- the same "wrong number
// that LOOKS real" failure class as $44,372.85-read-as-$0 and
// rank-0-reads-as-better-than-#1, just shaped differently: a delta between
// two adjacent DAYS rendered in the exact same "↑14%" shape as one between
// two adjacent MONTHS reads as the same claim to a viewer even though it
// isn't (Carl's ruling, 2026-08-09 -- googleadwords' cost/impressions/
// clicks over_time went daily; every other section's over_time is still
// monthly today, but the badge widget itself must not assume that forever).
// Mirrors computeTrendDeltaPct's own usable-points logic exactly (same
// periodEnd-aware trailing-partial-bucket exclusion) so the two can never
// disagree about which two points were actually compared.
function trendDeltaGranularity(
  points: TrendPoint[] | null | undefined,
  periodEnd?: string | null,
): "day" | "month" | null {
  if (!points || points.length < 2) return null;
  const usable = usableTrendPoints(points, periodEnd);
  if (usable.length < 2) return null;
  return isMonthlyBucketLabel(usable[usable.length - 1].date) ? "month" : "day";
}

// === Absurd-percentage floor (task #24) ===
// Live walk turned up meaningless figures rendered as if they were real
// signal: +32,773% (Tyler McBroom), +37,321% (Alamo City), +5,238% (ALIBI),
// and an attribution +1080% off a ~10-session prior base. Every one of
// these is arithmetically correct -- real numerator, real denominator --
// and every one is pure noise: the denominator (the prior-period value) is
// so small that a single-digit absolute move reads as a four/five-digit
// percentage. A reader can't distinguish "this metric really is up 300x"
// from "there were 3 sessions and now there are 4" without seeing the
// baseline, so past a certain point a bare percentage actively misleads --
// the same "a wrong-looking-right number is worse than a missing one"
// principle computeTrendDeltaPct/TrendSparkline already apply to partial
// trailing buckets above.
//
// Two tiers, because the prior absolute value isn't always available to the
// call site doing the rendering:
//   1. `BASELINE_FLOOR` -- for callers that DO have the real prior value in
//      hand (TrendBadge, which derives it from the same `points` series it
//      already diffs). 20 is chosen as "a small-but-real sample": the
//      ticket's own "~10-session prior base" example sits well under it,
//      while a metric that merely doubled off a base of 25+ keeps rendering
//      as a normal, trustworthy percentage badge.
//   2. `IMPLAUSIBLE_PCT_MAGNITUDE` -- the fallback for every OTHER badge in
//      this dashboard (ChangePctBadge, ~30 call sites across every frame,
//      plus InsightsStrip's InsightChip), none of which ever receive the
//      prior value at all: ChangePctBadge takes a bare `pct: number` with
//      no accompanying count, and InsightMover (AA's own Insights payload --
//      see types/clientAnalytics.ts) carries only `change_pct`, never the
//      underlying numbers it was computed from. Lacking a baseline to check
//      directly, this is a magnitude-only proxy for the identical
//      underlying problem: no real period-over-period marketing metric in
//      this dashboard (sessions/clicks/spend/leads/CPC/reviews/etc.)
//      legitimately moves 10x+ without an artificially tiny denominator
//      behind it. 999 (>= 10x) clears all four confirmed live examples with
//      wide margin while leaving a genuine double ("+100%") or triple
//      ("+300%") untouched.
//
// Both tiers live here, in primitives.tsx -- the one file every percentage
// badge in this dashboard already renders through (ChangePctBadge/TrendBadge
// directly; InsightsStrip.tsx duplicates just the IMPLAUSIBLE_PCT_MAGNITUDE
// constant+comment, not the rule, because that file's own header commits it
// to zero imports beyond React -- see that file for the cross-reference).
// The rule is decided once here, not recomputed differently in the mapper
// that builds InsightMover/AdEntityRow/etc. -- those already ship a bare
// already-divided percentage with no baseline attached, so there is nowhere
// upstream of this file that could apply `BASELINE_FLOOR` even if the rule
// lived there instead.
const BASELINE_FLOOR = 20;
const IMPLAUSIBLE_PCT_MAGNITUDE = 999;

export function isImplausiblePctMagnitude(pct: number): boolean {
  return Number.isFinite(pct) && Math.abs(pct) >= IMPLAUSIBLE_PCT_MAGNITUDE;
}

export function TrendBadge({
  points,
  periodEnd,
}: {
  points: TrendPoint[] | null | undefined;
  periodEnd?: string | null;
}) {
  const delta = computeTrendDeltaPct(points, periodEnd);
  if (delta === null) return null;
  // Only daily comparisons get an explicit suffix -- "DoD" (day-over-day).
  // Every other badge in the app is still month-over-month today, which is
  // the reader's existing default assumption for a bare "↑14%" here; adding
  // "MoM" to all of those too would be pure visual churn for zero new
  // information. The one case that silently changed meaning gets labeled.
  const suffix = trendDeltaGranularity(points, periodEnd) === "day" ? " DoD" : "";
  // Absurd-percentage floor, baseline-value tier (see comment above) -- we
  // have the real prior point right here, so use it rather than falling
  // back to the magnitude-only proxy. Below the floor, show the honest
  // absolute change instead of a percentage that a thin denominator would
  // blow out of proportion -- never hide that something moved, just stop
  // dressing it up as a specific-looking percentage it isn't.
  const usable = usableTrendPoints(points!, periodEnd);
  const prev = usable[usable.length - 2].value;
  if (Math.abs(prev) < BASELINE_FLOOR) {
    const absDelta = usable[usable.length - 1].value - prev;
    return (
      <Pill tone="neutral">
        {absDelta >= 0 ? "↑" : "↓"} {Math.abs(Math.round(absDelta)).toLocaleString()}{suffix} · thin baseline
      </Pill>
    );
  }
  return (
    <Pill tone={delta >= 0 ? "up" : "down"}>
      {delta >= 0 ? "↑" : "↓"} {Math.abs(Math.round(delta))}%{suffix}
    </Pill>
  );
}

// Per-metric colour polarity for change badges. Without this, every change
// badge in this file colored PURELY by arithmetic sign (positive=green,
// negative=red), silently assuming "up is good news" for every metric it
// was ever handed. That's backwards for anything where a SMALLER number is
// the win, and meaningless for a raw total where neither direction is
// inherently good or bad. Confirmed live defects this fixes (task #21,
// 2026-08-09/10 DEFECT-cpc-badge-polarity write-up):
//   - Avg CPC rising rendered GREEN across Google Ads (ALIBI, all 7
//     campaigns) and Meta Ads (Tyler McBroom), every entity level -- AA's
//     own reference report renders the identical value RED.
//   - Avg Position IMPROVING (the number going DOWN) rendered RED on AAA
//     Fence.
//
// Keyed by the metric's own base field name -- e.g. "avg_cpc" for
// `avg_cpc_change_pct` on both an AdEntityRow/AdsPeriodComparison (Google)
// and a MetaAdsCountryRow (Meta) -- callers pass that same stem as the
// `metric` prop, never the "*_change_pct" field name itself. Any metric NOT
// listed here defaults to "higher-is-better", which is both this
// dashboard's existing behavior and the correct read for the overwhelming
// majority of metrics these badges render (sessions/clicks/impressions/
// conversions/contacts/deals/revenue/CTR/ratings/reviews/engagement) -- so
// this list only needs entries for the metrics that must diverge from that
// default, not an exhaustive catalogue of every metric in the app.
export type MetricPolarity = "higher-is-better" | "lower-is-better" | "neutral";

// A smaller number is the win for all of these -- a badge coloring a RISE
// green would tell the client the opposite of the truth.
//   - avg_cpc / cost_per_conv: paying less per click/conversion is better
//     efficiency, not worse.
//   - bounce_rate: fewer single-page exits is better (no current
//     ChangePctBadge/MetricCell call site renders a bounce_rate badge as of
//     this fix -- HubSpotAttributionFrame's per-row bounce_rate is plain
//     text, no badge -- listed anyway so any future call site inherits the
//     right polarity by default instead of the wrong one).
//   - avg_position / search rank: position #1 beats position #10 (see
//     MultiEngineRankTable's RankCell and SearchPerformancePanel's
//     `positionImproved`, both of which already invert correctly via their
//     own bespoke logic, independent of these shared badges -- listed here
//     too so a future call site that switches to ChangePctBadge/MetricCell
//     doesn't have to rediscover this).
//   - search_rank_lost_impression_share / search_budget_lost_impression_share:
//     these measure impression share LOST -- rising is more opportunity
//     missed, not more of something good (no *_change_pct exists on
//     AdsSection for either today, so no live call site yet -- same
//     future-proofing rationale as above).
const LOWER_IS_BETTER_METRICS = new Set<string>([
  "avg_cpc",
  "cost_per_conv",
  "bounce_rate",
  "avg_position",
  "search_rank_lost_impression_share",
  "search_budget_lost_impression_share",
]);

// Rising spend is neither good nor bad news on its own -- more budget can
// mean more opportunity captured, or it can mean rising costs for flat
// results; a KPI-tile badge can't tell which without campaign-level context
// it doesn't carry. Rendered in the same neutral gray as "no data" (never
// green or red) rather than guessing a direction. `cost` covers both
// AdsSection.cost (Google) and MetaAdsSection.cost/AdEntityRow.cost (Meta) --
// same field name on every platform this dashboard reads.
const NEUTRAL_METRICS = new Set<string>(["cost", "spend", "ad_spend"]);

export function polarityForMetric(metric?: string | null): MetricPolarity {
  if (!metric) return "higher-is-better";
  if (NEUTRAL_METRICS.has(metric)) return "neutral";
  if (LOWER_IS_BETTER_METRICS.has(metric)) return "lower-is-better";
  return "higher-is-better";
}

// Shared sign-to-tone resolution for every change badge in this file that
// takes an already-computed metric. The ARROW always reflects the raw
// arithmetic direction of the number itself (rising=▲/↑, falling=▼/↓,
// matching AA's own convention of showing a literal-direction arrow even
// when it colors that same arrow as bad news) -- only the COLOR depends on
// polarity, never the direction shown.
export function changeTone(pct: number, metric?: string | null): "up" | "down" | "neutral" {
  const polarity = polarityForMetric(metric);
  if (polarity === "neutral") return "neutral";
  const rising = pct >= 0;
  const improved = polarity === "lower-is-better" ? !rising : rising;
  return improved ? "up" : "down";
}

// A badge for an ALREADY-COMPUTED period-over-period percentage -- e.g.
// AdsPeriodComparison's clicks_change_pct/impressions_change_pct/etc., or a
// per-row delta on a breakdown table. Distinct from TrendBadge above (which
// derives its own delta from a TrendPoint[] series): this one takes the
// number as-is and renders nothing when it's null/undefined/non-finite, the
// same never-fabricate contract every other badge in this file follows.
// Two decimals (matching AA's own "▲8.00%" reference precision and
// CrmContactsFrame's pre-existing local DeltaPill) rather than TrendBadge's
// whole-percent rounding, since these are already-precise account/row totals,
// not a coarse trend read.
//
// `metric` is OPTIONAL and additive -- every existing call site that omits
// it keeps today's higher-is-better sign coloring exactly as before (correct
// for the vast majority of metrics this renders); only the call sites for a
// metric in LOWER_IS_BETTER_METRICS/NEUTRAL_METRICS above need to start
// passing it.
export function ChangePctBadge({
  pct,
  metric,
}: {
  pct: number | null | undefined;
  metric?: string;
}) {
  if (pct === null || pct === undefined || !Number.isFinite(pct)) return null;
  // Absurd-percentage floor, magnitude tier -- see the comment above
  // TrendBadge for the full rationale and the four confirmed live examples
  // (+32,773%/+37,321%/+5,238%/+1080%) this catches. This is the ONLY tier
  // available here: ChangePctBadge is handed an already-divided `pct` with
  // no accompanying prior-period value, so there's nothing to check a
  // baseline threshold against -- direction (real, still trustworthy) stays
  // on the badge; the specific magnitude (not trustworthy past this point)
  // is replaced with an honest "thin baseline" disclosure instead of a
  // fabricated-looking five-digit number.
  if (isImplausiblePctMagnitude(pct)) {
    return (
      <Pill tone="neutral">
        {pct >= 0 ? "▲" : "▼"} thin baseline
      </Pill>
    );
  }
  return (
    <Pill tone={changeTone(pct, metric)}>
      {pct >= 0 ? "▲" : "▼"} {Math.abs(pct).toFixed(2)}%
    </Pill>
  );
}

// AA recommendation-block pattern (P3-3): the SHAPE only -- a deterministic,
// data-derived one-liner, never LLM/editorial prose (Carl's explicit
// guidance). This one worked example is the ticket's own spec: keywords
// that crossed into (or out of) the top-10 band between the first and most
// recent rank_distribution period. Returns null (optional slot, stays
// silent) when there's nothing to report -- not enough periods, or zero
// net movement.
export function computeTop10BandMovement(points: RankBandPoint[] | null | undefined): string | null {
  if (!points || points.length < 2) return null;
  const top10 = (p: RankBandPoint) => p.band_1_3 + p.band_4_10;
  const delta = top10(points[points.length - 1]) - top10(points[0]);
  if (delta === 0) return null;
  const verb = delta > 0 ? "moved into" : "dropped out of";
  return `${Math.abs(delta)} keyword${Math.abs(delta) === 1 ? "" : "s"} ${verb} the top 10 this period`;
}

export function InsightLine({ text }: { text: string | null }) {
  if (!text) return null;
  return (
    <div className="rounded-md bg-coral-light px-3 py-2 text-dash-context font-medium text-coral-dark">
      {text}
    </div>
  );
}

// Shared mini trend chart for section panels (leads/traffic/local/ads over
// time). Wraps the shared dashboard TrendChart so every panel gets the same
// look and the same graceful "not enough data" fallback — never a crash,
// never an empty axis box for a 0- or 1-point series.
export function TrendSparkline({
  points,
  height = 90,
  emptyMessage = "Not enough data yet",
  periodEnd,
}: {
  points: TrendPoint[] | null | undefined;
  height?: number;
  emptyMessage?: string;
  periodEnd?: string | null;
}) {
  if (!points || points.length < 2) {
    return (
      <div
        className="flex items-center justify-center text-dash-context text-muted-foreground"
        style={{ height }}
      >
        {emptyMessage}
      </div>
    );
  }
  // Omit a still-in-progress trailing bucket rather than plot it as a real
  // final point -- it would read as a cliff (see trailingBucketIsPartial
  // above). Same rule as the badge next to it.
  const usable = usableTrendPoints(points, periodEnd);
  if (usable.length < 2) {
    return (
      <div
        className="flex items-center justify-center text-dash-context text-muted-foreground"
        style={{ height }}
      >
        {emptyMessage}
      </div>
    );
  }
  const chartData = usable.map((p, i) => ({ idx: i, value: p.value }));
  return (
    <TrendChart
      data={chartData}
      series={[{ dataKey: "value" }]}
      xKey="idx"
      height={height}
      xTickFormatter={() => ""}
      showCurrentValue={false}
    />
  );
}

// A titled, axis-labelled trend chart with an optional paler "Previous
// Period" overlay -- the AA reference-report pattern (Meta Ads Clicks: a
// real daily current-period series against a same-length real prior-period
// series). Distinct from TrendSparkline above: this one shows REAL x-axis
// tick labels and a header total+badge, for the one or two places a panel
// needs a full chart rather than a blanked-axis mini trend.
//
// The "Previous Period" line comes from each point's own optional
// `prior_value` (see TrendPoint's docstring: day-offset-matched against a
// real second pull, never fabricated) -- not a separate parallel array, so
// there's no way for the two series to drift out of alignment. A point
// missing `prior_value` just leaves a gap in that one segment of the paler
// line rather than block the whole overlay.
export function TrendChartCard({
  title,
  value,
  badge,
  seriesName,
  points,
  height = 220,
}: {
  title: string;
  value?: ReactNode;
  badge?: ReactNode;
  // Legend/tooltip label for the primary series -- defaults to `title`.
  seriesName?: string;
  points: TrendPoint[] | null | undefined;
  height?: number;
}) {
  const hasChart = !!points && points.length >= 2;
  const hasPrevious = hasChart && points!.some((p) => p.prior_value != null);
  const primaryName = seriesName ?? title;

  return (
    <div className="rounded-lg border border-border bg-card p-4 flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <div className="text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
          {title}
        </div>
        {(value !== undefined || badge) && (
          <div className="flex items-center gap-2">
            {value !== undefined && (
              <span className="text-dash-value font-bold tabular">{value}</span>
            )}
            {badge}
          </div>
        )}
      </div>
      {hasPrevious && (
        <div className="flex items-center gap-4 text-dash-context text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <span className="h-0.5 w-3 rounded-full bg-[var(--color-chart-series-1)]" />
            {primaryName}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-0.5 w-3 rounded-full bg-[var(--color-chart-series-1)] opacity-40" />
            Previous Period
          </span>
        </div>
      )}
      {hasChart ? (
        <TrendChart
          data={points!.map((p) => ({
            date: p.date,
            value: p.value,
            previous: p.prior_value ?? undefined,
          }))}
          series={[
            { dataKey: "value", name: primaryName },
            ...(hasPrevious ? [{ dataKey: "previous", name: "Previous Period", muted: true }] : []),
          ]}
          xKey="date"
          height={height}
          xTickFormatter={formatDayTick}
          xAxisInterval={Math.max(0, Math.ceil(points!.length / 5) - 1)}
          showCurrentValue={false}
        />
      ) : (
        <div
          className="flex items-center justify-center text-dash-context text-muted-foreground"
          style={{ height }}
        >
          Not enough data yet
        </div>
      )}
    </div>
  );
}

// A titled section band with a source caption on the right.
export function SectionBand({
  title,
  caption,
  children,
}: {
  title: string;
  caption?: string;
  children: ReactNode;
}) {
  return (
    <section className="border-t border-border pt-6 mt-7">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-dash-title font-semibold text-foreground">{title}</h2>
        {caption && (
          <span className="text-dash-context text-muted-foreground">{caption}</span>
        )}
      </div>
      {children}
    </section>
  );
}

// One stat in a KPI strip.
export function StatBlock({
  label,
  value,
  context,
  hero = false,
  badge,
  sparkline,
  periodEnd,
}: {
  label: string;
  value: ReactNode;
  context?: string;
  hero?: boolean;
  badge?: ReactNode;
  /**
   * AA KPI-tile-as-sparkline pattern (P3-3): a compact trend line beneath
   * the value, reserved for exactly ONE hero tile system-wide (ad spend) --
   * do not retrofit every StatBlock, it becomes noise. Omitted entirely
   * (not even an empty-state placeholder) when the series can't support a
   * real line, so a data-thin tile never shows a fake or broken sparkline.
   */
  sparkline?: TrendPoint[] | null;
  // Dashboard's period_end (see ClientAnalyticsDashboard) -- forwarded to
  // TrendSparkline so a partial trailing month never plots as a cliff.
  periodEnd?: string | null;
}) {
  const hasSparkline = !!sparkline && sparkline.length >= 2;
  return (
    <div className="flex-1 p-5 flex flex-col gap-1">
      <div className="text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="flex items-center gap-2">
        <span
          className={
            (hero ? "text-[40px] leading-[40px]" : "text-dash-value") +
            " font-bold tabular"
          }
        >
          {value}
        </span>
        {badge}
      </div>
      {context && <div className="text-dash-context text-muted-foreground">{context}</div>}
      {hasSparkline && (
        <div className="mt-1">
          <TrendSparkline points={sparkline} height={32} periodEnd={periodEnd} />
        </div>
      )}
    </div>
  );
}

// Bare, axis-free line for HeroKpiTile's dark-card sparkline below. Deliberately
// NOT TrendSparkline (which wraps the shared TrendChart -- CartesianGrid +
// a real Y-axis with tick text, just with the X ticks blanked): that reads
// fine on a white bg-card row, but the Y-axis tick numerals show up
// unblanked against a solid dark tile and clutter the "tile IS the chart"
// look AA's own reference uses. A dedicated bare recharts primitive here,
// rather than adding a hideAxes escape hatch to the TrendChart every other
// panel also composes with. Reuses usableTrendPoints so the partial-
// trailing-bucket rule stays identical to every other sparkline in this file
// (a no-op for the daily series this actually renders, but keeps the
// contract consistent if a monthly series is ever passed in here too).
function HeroSparklineLine({
  points,
  periodEnd,
  height = 48,
}: {
  points: TrendPoint[];
  periodEnd?: string | null;
  height?: number;
}) {
  const usable = usableTrendPoints(points, periodEnd);
  if (usable.length < 2) return null;
  const chartData = usable.map((p, i) => ({ idx: i, value: p.value }));
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={chartData} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
        <Area
          type="monotone"
          dataKey="value"
          stroke="var(--color-ring)"
          fill="color-mix(in srgb, var(--color-ring) 22%, transparent)"
          strokeWidth={2}
          isAnimationActive={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

// AA "the tile IS the chart" black KPI hero (GA4 Traffic band reference
// screenshot, 2026-08-09): icon+label+badge header row, one big number, an
// accent-blue sparkline filling the lower half. A DELIBERATELY SEPARATE
// component from StatBlock above, not a StatBlock variant, for two reasons:
//   1. Layout differs, not just styling -- the badge sits in the HEADER row
//      next to the label here (AA's own reference), vs. next to the VALUE
//      in StatBlock; forcing that into StatBlock's existing layout via a
//      positional flag would be a worse fit than a dedicated component.
//   2. StatBlock's own `sparkline` doc reserves the pattern for exactly ONE
//      hero tile "system-wide" (ad spend) so it doesn't get retrofitted into
//      every tile and become noise. This screenshot shows THREE in one row
//      -- AA's own design already uses the pattern liberally here. Rather
//      than silently overriding that ruling (by bolting a 3rd meaning onto
//      StatBlock) or refusing to match the reference, this ships as its own
//      component and the conflict is flagged in the review request for the
//      ruling to be revisited on its actual merits, not decided by a chart
//      author under deadline.
//
// `bg-foreground text-background` (never a literal hex) keeps it theme-
// aware: near-black-on-white in light mode (matches the reference exactly),
// inverting to a bright card against the app's own dark background in dark
// mode -- still the highest-contrast "pop" tile in the row either way,
// which is the actual design intent, not a fixed color.
export function HeroKpiTile({
  label,
  value,
  icon,
  badge,
  sparkline,
  periodEnd,
}: {
  label: string;
  value: ReactNode;
  icon?: ReactNode;
  badge?: ReactNode;
  sparkline?: TrendPoint[] | null;
  periodEnd?: string | null;
}) {
  const hasSparkline = !!sparkline && sparkline.length >= 2;
  return (
    <div className="flex-1 min-w-0 rounded-lg bg-foreground text-background p-4 flex flex-col gap-2 overflow-hidden">
      <div className="flex items-center justify-between gap-2">
        <span className="flex min-w-0 items-center gap-1.5 text-dash-label font-semibold uppercase tracking-wide opacity-70 truncate">
          {icon}
          <span className="truncate">{label}</span>
        </span>
        {badge}
      </div>
      <div className="text-[28px] leading-[28px] font-bold tabular truncate">{value}</div>
      <div className="mt-auto -mb-1">
        {hasSparkline ? (
          <HeroSparklineLine points={sparkline!} periodEnd={periodEnd} height={48} />
        ) : (
          // Adaptive empty state: omit the chart area's HEIGHT too (not just
          // the line) would make a data-thin tile visibly shorter than its
          // siblings in the same row -- keep the spacer so all three tiles
          // stay the same height whether or not each has a real sparkline.
          <div style={{ height: 48 }} />
        )}
      </div>
    </div>
  );
}

// A horizontal KPI strip inside a card, blocks divided by vertical rules.
//
// `flex-wrap` (task #24-follow-up, confirmed live on Alamo City's Advertising
// tile row -- Conversions cut mid-digit, Return on Spend entirely off-
// screen): every StatBlock child keeps its default `min-width: auto`, i.e.
// the flexbox spec's automatic content-based minimum -- with `flex-1`
// (`flex-basis: 0%`), that's exactly what forces a `nowrap` row wider than
// its container to overflow the box instead of shrinking tiles below
// readable size. `flex-wrap` doesn't change that per-tile floor at all; it
// just lets the row spill onto a second line once the tiles' combined
// natural widths exceed the available width, rather than silently
// overflowing an ancestor's `overflow-x-hidden` main content column with no
// scrollbar and no way to reach the clipped tiles. Same fix already proven
// live in this directory for the identical `flex ... divide-x divide-border`
// pattern -- see CrmContactsFrame.tsx's `flex divide-x divide-border
// flex-wrap` and DealPipelinePanel.tsx's `flex flex-wrap divide-x
// divide-border`, both already shipping the same two-line KPI row with no
// separate min-width override needed on the tile itself.
export function KpiStrip({ children }: { children: ReactNode[] }) {
  return (
    <div className="mt-5 flex flex-wrap rounded-lg border border-border bg-card divide-x divide-border">
      {children}
    </div>
  );
}

// A labelled progress bar row (source / channel breakdowns).
export function BarRow({
  label,
  value,
  pct,
  tone = "primary",
}: {
  label: string;
  value: ReactNode;
  pct: number; // 0..100
  tone?: "primary" | "secondary";
}) {
  const fill = tone === "primary" ? "bg-coral" : "bg-teal";
  return (
    <div className="flex items-center gap-3">
      <div className="text-dash-subtitle w-40 shrink-0 truncate">{label}</div>
      <div className="flex-1 h-2 rounded bg-muted overflow-hidden">
        <div className={`${fill} h-2 rounded`} style={{ width: `${pct}%` }} />
      </div>
      <div className="text-dash-subtitle tabular w-14 text-right">{value}</div>
    </div>
  );
}

export function Pill({
  children,
  tone = "neutral",
}: {
  children: ReactNode;
  tone?: "up" | "down" | "neutral";
}) {
  const cls =
    tone === "up"
      ? "bg-[hsl(var(--status-success-bg))] text-[hsl(var(--status-success))]"
      : tone === "down"
        ? "bg-[hsl(var(--status-error-bg))] text-[hsl(var(--status-error))]"
        : "bg-muted text-muted-foreground";
  return (
    <span className={`${cls} rounded-full px-2 text-dash-context font-semibold whitespace-nowrap`}>
      {children}
    </span>
  );
}

// A small "data source" badge — panel-head chip naming where the numbers came from.
export function SourceChip({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md bg-muted px-2.5 py-0.5 text-dash-context font-semibold text-muted-foreground whitespace-nowrap">
      <span className="h-1.5 w-1.5 rounded-sm bg-[hsl(var(--status-success))]" />
      {label}
    </span>
  );
}

// The not-connected adaptive state for a section.
export function NotConnected({
  message,
  cta,
}: {
  message: string;
  cta?: string;
}) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-dashed border-border bg-muted/40 px-6 py-5">
      <div className="flex flex-col gap-1">
        <div className="text-dash-subtitle text-foreground">{message}</div>
        <div className="text-dash-context text-muted-foreground">
          When this data source is connected, it appears here automatically.
        </div>
      </div>
      {/* Informational text, not a button — there's no click target here yet. */}
      {cta && (
        <span className="text-dash-subtitle text-muted-foreground font-medium whitespace-nowrap">
          {cta}
        </span>
      )}
    </div>
  );
}
